import { VENUES_TAXONOMY_KEY } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import {
  CreateInquiryRequest,
  InquiryBudgetMatchFlowView,
  ReferenceVendorView,
} from '@zola/svc-marketplace-ts-types';

import _isEmpty from 'lodash/isEmpty';
import _omit from 'lodash/omit';
import _omitBy from 'lodash/omitBy';
import _pick from 'lodash/pick';

import { initializeExpandedWeddingDates } from '~/components/common/form/ExpandedDateFields/initializeExpandedWeddingDates';
import { allowsExpandedWeddingDates } from '~/pages/vendors/Inquiries/InquiryDetail/components/match/utils';
import { MappedInquiryPreferencesView } from '~/types/responseTypes';
import { CouplesStorefrontDetails } from '~/types/storefrontDetails';
import { formatStringToNumber } from '~/util/formatters';
import { centsToDollars, dollarsToCents } from '~/util/priceConversion';

import { spreadWeddingDate } from '../../../../components/common/form/WeddingDateField/dateFunctions';
import {
  BuildInquiryRequest,
  InquiryFormValues,
  InquiryFormFacets,
  InquiryServicesOffered,
  InquiryStepDefinition,
} from '../types';
import { formatFacetOptions } from './facets';
import { convertInquiryPreferencesToFormValues, vendorInquiryPreferencesKeys } from './preferences';

export const determineInitialValues = ({
  isGuest,
  storefront,
  preferences,
  bookedDates,
  venueReferenceVendor,
  questionnaire,
  servicesOffered,
  isRedesignedInquiryFlow,
}: {
  isGuest: boolean;
  preferences: MappedInquiryPreferencesView | undefined;
  storefront: Pick<CouplesStorefrontDetails, 'id' | 'taxonomyKey' | 'name' | 'uuid'>;
  bookedDates: string[];
  venueReferenceVendor: ReferenceVendorView | null;
  questionnaire: InquiryStepDefinition[];
  servicesOffered: InquiryServicesOffered;
  isRedesignedInquiryFlow?: boolean;
}): InquiryFormValues => {
  let vals: InquiryFormValues = {
    firstInquiryToCategory: !preferences?.inquiredSameCategoryBefore,

    // Phone can be in two questions, so we need to lift it into the form
    phone: preferences?.phone ?? undefined,
  };

  // set to values from past inquiry or onboarding
  // data if they exist
  if (!isGuest && preferences) {
    /**
     * we want to strip out values from inquiry preferences we don't need
     * the list of values needed is different for every vendor type
     */
    vals = {
      ...vals,
      ...convertInquiryPreferencesToFormValues(storefront.taxonomyKey, preferences),
    };
  }

  questionnaire.forEach((question) => {
    if (question.initializeFormValues) {
      vals = question.initializeFormValues(
        { preferences, venueReferenceVendor, servicesOffered, storefront },
        vals
      );
    }
  });

  // format wedding date to support the M/D/Y form dropdowns
  if (preferences?.weddingDate || preferences?.weddingYearMonthDate) {
    const date = spreadWeddingDate(preferences?.weddingDate, preferences?.weddingYearMonthDate);
    vals = {
      ...vals,
      ...date,
    };
  }

  // If we have a validated location from the couple's wedding preferences use that
  // otherwise, use a location from a previous inquiry.  We don't promote inquiry
  // wedding location into wedding preferences so for marketplace first couples
  // making multiple inquiries, without this fallback to inquiry preferences, they
  // have to enter the wedding location on each inquiry.
  if (!vals.weddingLocation) {
    vals.weddingLocation = preferences?.weddingLocation || undefined;
  }

  // should strip out facets that we previously collected but no longer do
  if (vals.inquiryFormFacets) {
    vals.inquiryFormFacets = _omit(vals.inquiryFormFacets, [
      'FLORIST_SERVICES_PRE_WEDDING',
      'FLORIST_SERVICES_DURING_WEDDING',
      'FLORIST_SERVICES_POST_WEDDING',
      'PACKAGE_DELIVERABLES_OFFERED',
      'PACKAGE_COMPONENTS_SERVICES_OFFERED',
    ]);
  }

  // add booked dates so we have access to it for validation purposes.  To move
  // this to the context we somehow need to pass the booked dates into the
  // field validator
  if (bookedDates.length > 0) {
    vals = { ...vals, bookedDates };
  }

  if (storefront.taxonomyKey === VENUES_TAXONOMY_KEY) {
    // value to control whether the user sees the flexible date selection or exact date selection
    const showFlexibleDateSelection = _isEmpty(preferences?.weddingDates?.exactDates);
    vals = { ...vals, showFlexibleDateSelection };
  }

  vals.weddingEndPrice = centsToDollars(vals.weddingEndPriceCents || null);

  // Radio inputs cannot take booleans as values, therefore we have to convert to strings so the radios will get populated with initial values
  if (typeof vals.flexibleOnPrice === 'boolean') {
    if (vals.flexibleOnPrice) {
      vals.flexibleOnPrice = 'flexible-on-price';
    } else {
      vals.flexibleOnPrice = 'budget-is-not-flexible';
    }
  }

  if (allowsExpandedWeddingDates(storefront.taxonomyKey)) {
    vals = initializeExpandedWeddingDates(vals, preferences);
  }
  if (isRedesignedInquiryFlow) {
    vals.flexibleOnDate = preferences?.flexibleOnDate || undefined;
  }

  return vals;
};

export const buildInquiryRequest = ({
  values,
  emailAddress,
  storefront,
  ctaPlacement,
  questionnaire,
  matchFlowView,
}: {
  values: InquiryFormValues;
  emailAddress: string;
  storefront: CouplesStorefrontDetails;
  ctaPlacement?: CreateInquiryRequest.CtaPlacementEnum;
  questionnaire: InquiryStepDefinition[];
  matchFlowView: InquiryBudgetMatchFlowView;
}): CreateInquiryRequest => {
  /**
   * vendorInquiryPreferencesKeys grabs all the valid keys from inquiry preferences response
   * omitKeys will remove the keys that are not needed
   * add priceMatch (this is needed, it is the result of the budget match screen)
   */
  const { preferences } = vendorInquiryPreferencesKeys(storefront.taxonomyKey);

  const adjustedFormFacets: InquiryFormFacets = _omitBy(values.inquiryFormFacets, (value, key) => {
    // Ignore facets that are handled in the questions
    return questionnaire.some((q) => q.buildInquiryRequest && q.facets && q.facets.includes(key));
  });

  const budgetFields = {
    serviceStartPriceCents: undefined,
    serviceEndPriceCents: dollarsToCents(values.serviceEndPrice || null),
  };

  const omitKeys = [
    'weddingMonth',
    'weddingDay',
    'weddingYear',
    'bookedDates',
    'weddingLocation',
    'serviceStartPrice',
    'serviceEndPrice',
    'weddingEndPrice',
    'priceMatch',

    // These fields are part of the preferences / form, but are not part of the inquiry create request
    'notListedVenueName',
    'venueChosenId',
    'venueChosenName',
    'venueName',
  ];
  const cleanedFormValues = _pick(values, [
    ...preferences.filter((pref) => !omitKeys.includes(pref)),
  ]);

  const flexibleOnPrice = values.flexibleOnPrice === 'flexible-on-price';

  let inquiry: CreateInquiryRequest = {
    ...cleanedFormValues,
    priceMatch: values.priceMatch ?? 'ORIGINAL_BUDGET_ACCEPTED',
    flexibleOnPrice,
    storefrontId: storefront.id,
    emailAddress,
    weddingEndPriceCents: dollarsToCents(formatStringToNumber(values.weddingEndPrice) || null),
    ...budgetFields,

    planningStage: null,
    vendorOptions: formatFacetOptions(adjustedFormFacets),
    ...(values.weddingMarket ? { vendorMarketId: values.weddingMarket.id } : undefined),

    ctaPlacement: ctaPlacement ?? null,

    notes: values.notes ?? null,

    // Phone can appear in two steps: wedding info or inquiry note, so we lift it into the form
    phone: values.phone,
  } as unknown as CreateInquiryRequest;

  questionnaire
    .filter((step) => step.buildInquiryRequest)
    .filter((step) => {
      if (step.isSkipped) {
        return !step.isSkipped({ storefront, values, matchFlowView });
      }
      return true;
    })
    .forEach((step) => {
      inquiry = (step.buildInquiryRequest as BuildInquiryRequest)({ values, inquiry });
    });

  return inquiry;
};
